/* eslint eslint-comments/no-use: off */
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {clsx} from 'clsx'
import {useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {ProjectItemSectionFields$key} from './__generated__/ProjectItemSectionFields.graphql'
import {DateField, type DateFieldFragment} from './fields/DateField'
import {IterationField, type IterationFieldFragment} from './fields/IterationField'
import {MultiSelectField} from './fields/MultiSelectField'
import {NumberField, type NumberFieldConfigFragment, type NumberFieldFragment} from './fields/NumberField'
import {
  SingleSelectField,
  type SingleSelectFieldConfigFragment,
  type SingleSelectFieldFragment,
} from './fields/SingleSelectField'
import {TextField, type TextFieldFragment} from './fields/TextField'
import type {ProjectItemNode, ProjectIterationFieldNode} from './project-item-section-fields-query-types'
import styles from './ProjectsSections.module.css'

type ProjectItemSectionFieldsProps = {
  projectItem: ProjectItemSectionFields$key
  onIssueUpdate: () => void
}

// RELAY-CLEANUP(ProjectItemSectionFields): widens nullable fields so the still-Relay parent data is assignable.
// Remove when ProjectItemSectionFields leaves Relay.
export type ProjectItemSectionFieldsValuesFragment = RelayCompat<Pick<ProjectItemNode, 'fieldValues'>>

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ProjectItemSectionFieldsFragment = graphql`
  fragment ProjectItemSectionFields on ProjectV2Item {
    id
    isArchived
    project {
      id
      title
      url
      viewerCanUpdate
      fields(first: 50, orderBy: {field: POSITION, direction: ASC}) {
        edges {
          node {
            ... on ProjectV2Field {
              id
              dataType
              name
              isIssueField
            }
            ... on ProjectV2IterationField {
              id
              dataType
              name
              isIssueField
              configuration {
                iterations {
                  id
                  title
                  titleHTML
                  startDate
                  duration
                }
                completedIterations {
                  id
                  title
                  titleHTML
                  startDate
                  duration
                }
              }
            }
            ... on ProjectV2SingleSelectField {
              id
              dataType
              name
              isIssueField
              options {
                id
                optionId
                name
                nameHTML
                color
                descriptionHTML
                # The status field has a non unique id. We leverage the getDataID method in the Relay environment,
                # and in this instance use the id, name, description and color to generate a unique ID:
                # - https://github.com/github/github/pull/305841/files#diff-7187f5ed804646bc353b5176a059d44bcd6a4cd5d5f19976aebb885d1e6997b8R155
                description
              }
            }
            ... on ProjectV2MultiSelectField {
              id
              dataType
              name
              isIssueField
              ...MultiSelectFieldConfigFragment @dangerously_unaliased_fixme
            }
          }
        }
      }
    }
    fieldValues(first: 50, orderBy: {field: POSITION, direction: ASC})
      @connection(key: "ProjectItemSection_fieldValues") {
      edges {
        node {
          ... on ProjectV2ItemFieldTextValue {
            id
            text
            field {
              ... on ProjectV2Field {
                id
              }
            }
          }
          ... on ProjectV2ItemFieldNumberValue {
            id
            number
            field {
              ... on ProjectV2Field {
                id
              }
            }
          }
          ... on ProjectV2ItemFieldDateValue {
            id
            date
            field {
              ... on ProjectV2Field {
                id
              }
            }
          }
          ... on ProjectV2ItemFieldSingleSelectValue {
            id
            field {
              ... on ProjectV2SingleSelectField {
                id
              }
            }
            optionId
            name
            nameHTML
            color
          }
          ... on ProjectV2ItemFieldIterationValue {
            id
            iterationId
            title
            titleHTML
            startDate
            duration
            field {
              ... on ProjectV2IterationField {
                id
                name
                configuration {
                  iterations {
                    id
                    title
                    titleHTML
                    startDate
                    duration
                  }
                  completedIterations {
                    id
                    title
                    titleHTML
                    startDate
                    duration
                  }
                }
              }
            }
          }
          ... on ProjectV2ItemFieldMultiSelectValue {
            id
            field {
              ... on ProjectV2MultiSelectField {
                id
              }
            }
          }
          ...MultiSelectFieldFragment @dangerously_unaliased_fixme
        }
      }
    }
  }
`

export function ProjectItemSectionFields({projectItem, onIssueUpdate}: ProjectItemSectionFieldsProps) {
  const projectItemFields = useFragment(ProjectItemSectionFieldsFragment, projectItem)

  const {fieldValues} = projectItemFields

  const fields = useMemo(
    () =>
      (projectItemFields?.project.fields?.edges || [])
        .map(edge => {
          // Issue fields are not rendered in the project item field list until there is a way to edit them and
          // improved GraphQL support.
          if (!edge?.node || edge.node.isIssueField || edge.node.name === 'Status') {
            return
          }

          const field = edge.node
          const dataType = field.dataType

          const fieldId = field.id
          const value =
            (fieldValues?.edges || []).flatMap(e => (e?.node ? [e.node] : [])).find(e => e?.field?.id === fieldId) ||
            null

          const props = {
            viewerCanUpdate: !projectItemFields.isArchived && projectItemFields.project.viewerCanUpdate,
            itemId: projectItemFields.id,
            projectId: projectItemFields.project.id,
            field,
            value,
            onIssueUpdate,
          }

          switch (dataType) {
            case 'SINGLE_SELECT':
              // RELAY-CLEANUP(ProjectItemSectionFields): value is narrowed from the still-Relay parent and its
              // color enum remains open. Remove when ProjectItemSectionFields leaves Relay.
              return (
                <SingleSelectField
                  key={fieldId}
                  {...props}
                  // RELAY-CLEANUP(ProjectItemSectionFields): field/value are narrowed from the still-Relay parent
                  // and color enums remain open. Remove when ProjectItemSectionFields leaves Relay.
                  field={field as SingleSelectFieldConfigFragment}
                  value={value as SingleSelectFieldFragment | null}
                />
              )
            case 'TEXT':
              // RELAY-CLEANUP(ProjectItemSectionFields): value is optional/widened while the parent is
              // Relay-owned. Remove when ProjectItemSectionFields leaves Relay.
              return <TextField key={fieldId} {...props} value={value as TextFieldFragment | null} />
            case 'DATE':
              // RELAY-CLEANUP(ProjectItemSectionFields): value is optional/widened while the parent is
              // Relay-owned. Remove when ProjectItemSectionFields leaves Relay.
              return <DateField key={fieldId} {...props} value={value as DateFieldFragment | null} />
            case 'NUMBER':
              // RELAY-CLEANUP(ProjectItemSectionFields): id/name remain optional and the value is narrowed
              // from the still-Relay parent data. Remove when ProjectItemSectionFields leaves Relay.
              return (
                <NumberField
                  key={fieldId}
                  {...props}
                  field={field as NumberFieldConfigFragment}
                  value={value as NumberFieldFragment | null}
                />
              )
            case 'ITERATION':
              // RELAY-CLEANUP(ProjectItemSectionFields): field/value are narrowed from the still-Relay parent data.
              // Remove when ProjectItemSectionFields leaves Relay.
              return (
                <IterationField
                  key={fieldId}
                  {...props}
                  field={field as Pick<ProjectIterationFieldNode, 'id' | 'name' | 'configuration'>}
                  value={value as IterationFieldFragment | null}
                />
              )
            case 'MULTI_SELECT': {
              return <MultiSelectField key={fieldId} {...props} />
            }
          }
        })
        .filter(i => i !== undefined),
    [
      projectItemFields.project.fields?.edges,
      projectItemFields.project.viewerCanUpdate,
      projectItemFields.project.id,
      projectItemFields.isArchived,
      projectItemFields.id,
      fieldValues?.edges,
      onIssueUpdate,
    ],
  )

  return fields.length === 0 ? (
    <> </>
  ) : (
    <ul className={clsx(styles.FieldListUl, styles.FieldListInternal)}>
      {fields.map((field, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <li className={styles.FieldListLi} key={`project-field-${index}`}>
          {field}
        </li>
      ))}
    </ul>
  )
}
